"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";

const AtlasFaqsSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const raw = t("atlasFaqs.items", { returnObjects: true });
  const faqs = Array.isArray(raw) ? raw : [];
  const isOdd = faqs.length % 2 === 1;

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, sm: 8, md: 10 },
        backgroundColor: "#FFFFFF",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        {/* Heading */}
        <Typography
          variant="h2"
          sx={{
            textAlign: "center",
            fontSize: { xs: "24px", sm: "30px", md: "34px", lg: "38px" },
            fontWeight: 700,
            lineHeight: 1.25,
            color: "#14122B",
            fontFamily: "var(--font-plusjakarta)",
            mb: { xs: 4, md: 6 },
          }}
        >
          {t("atlasFaqs.heading")}
        </Typography>

        {/* FAQ cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: { xs: 2, md: 2.5 },
            mb: { xs: 4, md: 5 },
          }}
        >
          {faqs.map((faq, index) => {
            const isLastFull = isOdd && index === faqs.length - 1;

            return (
              <Box
                key={index}
                sx={{
                  gridColumn: { xs: "auto", md: isLastFull ? "1 / -1" : "auto" },
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #EDE6FA",
                  borderRadius: "14px",
                  p: { xs: 2.5, md: 3 },
                  textAlign: isArabic ? "right" : "left",
                  boxShadow: "0 2px 12px rgba(122,43,245,0.04)",
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    fontSize: { xs: "15px", md: "16px" },
                    fontWeight: 700,
                    lineHeight: 1.4,
                    color: "#14122B",
                    fontFamily: "var(--font-plusjakarta)",
                    mb: { xs: 1.25, md: 1.5 },
                  }}
                >
                  {faq.question}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "12.5px", md: "13px" },
                    color: "#3D3A57",
                    lineHeight: 1.75,
                    fontWeight: 400,
                    fontFamily: "var(--font-outfit)",
                  }}
                >
                  {faq.answer}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* CTA */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <NextLink
            href="/free-trial-of-ai-recruiting-tool"
            passHref
            legacyBehavior
          >
            <Button
              component="a"
              sx={{
                bgcolor: "#7A2BF5",
                color: "white",
                px: { xs: 3, sm: 3.5 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                whiteSpace: "nowrap",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#7A2BF5",
                  transform: "scale(1.03)",
                },
              }}
            >
              {t("atlasFaqs.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default AtlasFaqsSection;